import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { useOrpcTanstackQueryUtils } from '../../orpc'
import { DutyStatus } from '@@api/router'

export function useRosterGetAll(
  filters?: {
    startDate?: string // YYYY-MM-DD
    endDate?: string // YYYY-MM-DD
    aircraftIds?: string[]
  },
  options?: { keepPreviousData?: boolean }
) {
  const orpc = useOrpcTanstackQueryUtils()
  return useQuery(
    orpc.roster.getAll.queryOptions({
      input: filters,
      // Opt-in: keep the prior range's data visible while a wider range loads
      // (used by the Gantt's continuous scroll so cells don't blink to empty).
      ...(options?.keepPreviousData ? { placeholderData: keepPreviousData } : {}),
    })
  )
}

// All roster entries and flight days, slim, for the Gantt's one-time load.
// Invalidated with the rest of `orpc.roster.key()` by every roster/trip mutation.
export function useRosterGantt() {
  const orpc = useOrpcTanstackQueryUtils()
  return useQuery(orpc.roster.getGantt.queryOptions())
}

export function useRosterHasAny() {
  const orpc = useOrpcTanstackQueryUtils()
  return useQuery(orpc.roster.hasAny.queryOptions())
}

export function useRosterGetById(id?: string | null) {
  const orpc = useOrpcTanstackQueryUtils()
  return useQuery(
    orpc.roster.getById.queryOptions({
      input: { id: id! },
      enabled: !!id,
    })
  )
}

export function useRosterGetByCrew(
  crewId: string,
  filters?: {
    startDate?: string // YYYY-MM-DD
    endDate?: string // YYYY-MM-DD
    dutyStatus?: DutyStatus
  }
) {
  const orpc = useOrpcTanstackQueryUtils()
  return useQuery(
    orpc.roster.getByCrew.queryOptions({ input: { crewId, ...filters } })
  )
}

export function useRosterGetSummary(
  crewId: string,
  startDate?: string,
  endDate?: string
) {
  const orpc = useOrpcTanstackQueryUtils()
  return useQuery(
    orpc.roster.getSummary.queryOptions({
      input: { crewId, startDate, endDate },
    })
  )
}

export function useRosterGetCompanySummary(
  startDate: string,
  endDate: string
) {
  const orpc = useOrpcTanstackQueryUtils()
  return useQuery(
    orpc.roster.getCompanySummary.queryOptions({
      input: { startDate, endDate },
    })
  )
}

export function useRosterGetHistory(crewId: string | null) {
  const orpc = useOrpcTanstackQueryUtils()
  return useQuery(
    orpc.roster.getRosterHistory.queryOptions({
      input: { crewId: crewId! },
      enabled: !!crewId,
    })
  )
}
